<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { makeDraggable, makeDroppable } from "@vue-dnd-kit/core"
import type { IDragEvent } from "@vue-dnd-kit/core"
import { useResponsive } from "@core/composables/useResponsive"
import { useAuthStore } from "@core/stores/auth.store"
import { useHomeWorkspaceStore } from "@modules/home/stores/homeStore.store"
import {
    applyDropToFolder,
    DASHBOARD_WORKSPACE_DND_GROUP,
    type WorkspaceDragPayload,
} from "@modules/home/composables/homeDnDHandler"
import type { FolderListRow } from "@modules/home/stores/home-list.types"

defineOptions({
    name: "HomeFolderRow",
})

// ================================
// TYPES & INTERFACES
// ================================
interface Props {
    folder: FolderListRow
    disabledMove?: boolean
    disabledRemove?: boolean
}

type ContextMenuExpose = {
    show: (event: MouseEvent) => void
}

/** Expose `ItemFolder` utilisé pour mesurer l’ellipse du nom. */
type ItemFolderExpose = {
    nameSpanRef: HTMLElement | null
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    disabledMove: false,
    disabledRemove: false,
})

// ================================
// COMPOSABLES
// ================================
const route = useRoute()
const router = useRouter()
const { isTablet } = useResponsive()
const homeWorkspace = useHomeWorkspaceStore()
const authStore = useAuthStore()

// ================================
// STATE
// ================================
const contextMenu = ref<ContextMenuExpose | null>(null)
const contextMenuModel = ref<{ label: string; command?: () => void }[]>([])

/** Même nœud pour draggable + droppable (vue-dnd-kit : placement « center »). */
const rowRef = ref<HTMLElement | null>(null)
const itemFolderRef = ref<ItemFolderExpose | null>(null)
const suppressFolderNavClick = ref(false)
/** True lorsque le nom est tronqué (ellipse) — le tooltip n’est actif que dans ce cas. */
const isFolderNameTruncated = ref(false)

let folderNameResizeObserver: ResizeObserver | null = null

const SUPPRESS_NAV_CLICK_FALLBACK_MS = 250

// ================================
// COMPUTED
// ================================
/** Pas de DnD sur tablette / mobile : désactivé sous le breakpoint `lg` (aligné Tailwind). */
const getDragDisabled = computed(() => props.disabledMove || isTablet.value)

const getFolderDropDisabled = computed(() => isTablet.value)

/** Options tooltip : désactivé tant que le texte n’est pas ellipsé. */
const getFolderNameTooltipOptions = computed(() => ({
    value: props.folder.name,
    disabled: !isFolderNameTruncated.value,
}))

/** Données affichées par la ligne récap dossier (nom + avatars accès). */
const getItemFolderData = computed(() => ({
    _id: props.folder._id,
    name: props.folder.name,
    visible_to: props.folder.visible_to,
}))

/**
 * Suppression réservée aux admin/owner (table des droits DEV-16282 : « Création
 * et suppression de bâtiments et de sources » = Admin/Owner uniquement). Un
 * dossier groupe des bâtiments → suppression destructive, même règle que le
 * bâtiment (cf. `HomeBuildingRow.getCanDeleteBuilding`). `ItemFolder` n'expose
 * pas de droit `owns` par dossier → on gate sur le seul rôle entreprise.
 */
const getCanDeleteFolder = computed(() => authStore.isCompanyAdmin)

const { isDragging } = makeDraggable(rowRef, {
    groups: [DASHBOARD_WORKSPACE_DND_GROUP],
    activation: { distance: 8 },
    disabled: getDragDisabled,
    /**
     * Même nœud draggable + droppable : sans zone « center », vue-dnd-kit
     * n’enregistre pas la droppable au survol (onDrop jamais appelé).
     * Marges nulles = toute la carte = center.
     */
    placementMargins: { top: 0, right: 0, bottom: 0, left: 0 },
    data: (): WorkspaceDragPayload => ({
        type: "folder",
        folderId: props.folder._id,
    }),
    events: {
        onSelfDragEnd: armSuppressNavClickAfterDrag,
        onSelfDragCancel: armSuppressNavClickAfterDrag,
    },
})

const { isAllowed: folderDropAllowed, isDragOver: folderDragOver } =
    makeDroppable(rowRef, {
        groups: [DASHBOARD_WORKSPACE_DND_GROUP],
        disabled: getFolderDropDisabled,
        data: () => ({ folderId: props.folder._id }),
        events: {
            onDrop(e: IDragEvent) {
                const src = e.draggedItems[0]?.data as
                    | WorkspaceDragPayload
                    | undefined
                if (!src) return
                applyDropToFolder(homeWorkspace, props.folder._id, src)
            },
        },
    })

/** Survol d’une zone de dépôt valide — halo vert (équivalent legacy `drag-over`). */
const getFolderRowDropOverClass = computed(() => {
    if (!folderDragOver.value || !folderDropAllowed.value) return ""
    return "shadow-[0_0_0_2px_rgba(34,197,94,0.45),0_6px_20px_rgba(34,197,94,0.18)]"
})

// ================================
// BUSINESS LOGIC
// ================================
/**
 * Met à jour l’indicateur d’ellipse sur le nom (active le tooltip seulement si tronqué).
 */
function updateFolderNameTruncation(): void {
    const el = itemFolderRef.value?.nameSpanRef
    if (!el) {
        isFolderNameTruncated.value = false
        return
    }
    isFolderNameTruncated.value = el.scrollWidth > el.clientWidth
}

function armSuppressNavClickAfterDrag(): void {
    suppressFolderNavClick.value = true
    window.setTimeout(() => {
        suppressFolderNavClick.value = false
    }, SUPPRESS_NAV_CLICK_FALLBACK_MS)
}

/**
 * Entrées du menu actions dossier (aligné sur la liste + « Ouvrir »).
 * @returns Modèle PrimeVue pour `BaseContextMenu`
 */
function buildFolderMenuItems(): { label: string; command?: () => void }[] {
    const folder = props.folder
    return [
        {
            label: "Ouvrir",
            command: () => router.push({ path: `/folder/${folder._id}` }),
        },
        {
            label: "Renommer",
            command: () =>
                homeWorkspace.openModal("folderRename", {
                    folderId: folder._id,
                    name: folder.name,
                }),
        },
        ...(!props.disabledMove
            ? [
                  {
                      label: "Déplacer",
                      command: () =>
                          homeWorkspace.openModal("folderMove", {
                              fromFolderId: folder._id,
                              currentFolderId: route.params.folderId as string,
                          }),
                  },
              ]
            : []),
        ...(!props.disabledRemove && getCanDeleteFolder.value
            ? [
                  {
                      label: "Supprimer",
                      command: () =>
                          homeWorkspace.openModal("folderRemove", {
                              folderId: folder._id,
                              name: folder.name,
                          }),
                  },
              ]
            : []),
        {
            label: "Partage",
            command: () =>
                homeWorkspace.openModal("folderAccess", {
                    folderId: folder._id,
                    itemFolder: {
                        _id: folder._id,
                        name: folder.name,
                        visible_to: folder.visible_to,
                    },
                }),
        },
    ]
}

// ================================
// WATCHERS
// ================================
watch(
    () =>
        [props.folder._id, props.folder.name, props.folder.visible_to] as const,
    () => {
        void nextTick(updateFolderNameTruncation)
    }
)

// ================================
// LIFECYCLE
// ================================
onMounted(() => {
    void nextTick(() => {
        updateFolderNameTruncation()
        const el = itemFolderRef.value?.nameSpanRef
        if (!el) return
        folderNameResizeObserver = new ResizeObserver(() => {
            updateFolderNameTruncation()
        })
        folderNameResizeObserver.observe(el)
    })
})

onBeforeUnmount(() => {
    folderNameResizeObserver?.disconnect()
    folderNameResizeObserver = null
})

// ================================
// INTERACTIONS
// ================================
/**
 * Affiche le menu (bouton ⋮ ou clic droit).
 * @param ev - Cible du menu
 */
function onToggleFolderMenu(ev: Event): void {
    contextMenuModel.value = buildFolderMenuItems()
    contextMenu.value?.show(ev as MouseEvent)
}

/**
 * Remplace le menu navigateur par le menu actions dossier.
 * @param ev - Clic droit
 */
function onFolderRowContextMenu(ev: MouseEvent): void {
    ev.preventDefault()
    onToggleFolderMenu(ev)
}

function onFolderRowClickCapture(ev: MouseEvent): void {
    if (!suppressFolderNavClick.value) return
    ev.preventDefault()
    ev.stopPropagation()
    suppressFolderNavClick.value = false
}

function onFolderClick(ev: Event): void {
    if (suppressFolderNavClick.value) {
        ev.preventDefault()
        ev.stopPropagation()
        suppressFolderNavClick.value = false
        return
    }
    router.push({ path: `/folder/${props.folder._id}` })
}

/**
 * Clic sur le récap dossier (`ItemFolder` en bouton natif) : navigation comme la ligne.
 * @param _folderId - Id dossier (émis par `ItemFolder`)
 * @param ev - Événement clic
 */
function onFolderItemClick(_folderId: string, ev?: MouseEvent): void {
    if (!ev) return
    onFolderClick(ev)
}
</script>

<template>
    <!-- ================================ -->
    <!-- LIGNE DOSSIER (vue-dnd-kit) -->
    <!-- ================================ -->
    <li class="dashboard-folder-row list-none">
        <div
            ref="rowRef"
            class="box-border flex h-[4rem] min-h-[4rem] max-h-[4rem] w-full min-w-0 items-center justify-between rounded-lg border border-surface-200 bg-surface-0 px-3.5 shadow-sm transition-[opacity,box-shadow] duration-300 ease-in-out cursor-pointer"
            @click.capture="onFolderRowClickCapture"
            @contextmenu="onFolderRowContextMenu"
            :class="[
                { 'opacity-0': isDragging },
                getFolderRowDropOverClass,
                !props.disabledMove
                    ? 'touch-none cursor-grab active:cursor-grabbing'
                    : '',
            ]"
        >
            <div
                class="flex min-h-0 min-w-0 flex-1 items-center justify-between overflow-hidden"
            >
                <div class="min-h-0 min-w-0 flex-1 overflow-hidden">
                    <ItemFolder
                        ref="itemFolderRef"
                        class="h-full min-w-0"
                        :folder="getItemFolderData"
                        :name-tooltip="getFolderNameTooltipOptions"
                        native-button
                        @click="onFolderItemClick"
                    />
                </div>
                <div class="flex items-center gap-1">
                    <BaseButton
                        icon="pi pi-ellipsis-v"
                        severity="secondary"
                        text
                        rounded
                        type="button"
                        aria-label="Actions dossier"
                        @click="onToggleFolderMenu"
                    />
                </div>
            </div>
        </div>
        <BaseContextMenu ref="contextMenu" :model="contextMenuModel" />
    </li>
</template>
